/**
 * Registry push destinations ("Push to ECR / GCR / Docker Hub / Azure ACR").
 *
 * The repository targets are pre-configured by a DevOps user in Account
 * Settings and tagged with a prod/dev alias; the operator just picks one by
 * alias in the Download Model modal. The concrete URI + auth mode are
 * resolved from the chosen target and sent to the backend (`sharedConfig`).
 */

const IMAGE_TAG_HINT = 'Tag: {task-type}-{project-name}-{task-name}-v{version}.';

export const REGISTRY_TARGETS = {
  ecr: {
    title: 'ECR target',
    description:
      'The inference image is built and pushed to the selected ECR repository. The image tag is set automatically to {task type}-{project name}-{task name}-v{version}.',
    tooltip:
      'Targets are configured by your DevOps team in Account Settings and selected here by environment alias.',
    labelTooltip: 'Pre-configured ECR destination, identified by its prod/dev alias.',
    placeholder: 'Select an ECR target',
    emptyHint:
      'No ECR targets configured. Ask a DevOps admin to add one under Account Settings → ECR push targets.',
    settingsKey: 'aws_ecr_targets',
    requiredField: 'uri',
    sharedConfig: (target) => ({
      ecr_repository_uri: (target.uri || '').trim(),
      ecr_account_mode: target.account_type || 'same_account',
      ecr_alias: target.alias || null,
    }),
    summary: (target) =>
      `Target: ${target.alias || 'dev'} (${
        target.account_type === 'cross_account' ? 'cross-account' : 'same-account'
      }). ${IMAGE_TAG_HINT}`,
  },
  gcr: {
    title: 'GCR target',
    description:
      'The inference image is built and pushed to the selected GCP Artifact Registry / GCR repository. The image tag is set automatically to {task type}-{project name}-{task name}-v{version}.',
    tooltip:
      'Targets are configured by your DevOps team in Account Settings (GCP - GCR) and selected here by environment alias.',
    labelTooltip: 'Pre-configured GCR destination, identified by its prod/dev alias.',
    placeholder: 'Select a GCR target',
    emptyHint:
      'No GCR targets configured. Ask a DevOps admin to add one under Account Settings → External Deploy - Targets (GCP - GCR).',
    settingsKey: 'gcp_gcr_targets',
    requiredField: 'uri',
    sharedConfig: (target) => ({
      gcr_repository_uri: (target.uri || '').trim(),
      gcr_auth_mode: target.auth_mode || 'same_project',
      gcr_alias: target.alias || null,
    }),
    summary: (target) =>
      `Target: ${target.alias || 'dev'} (${
        target.auth_mode === 'service_account' ? 'service-account' : 'same-project'
      }). ${IMAGE_TAG_HINT}`,
  },
  dockerhub: {
    title: 'Docker Hub target',
    description:
      'The inference image is built and pushed to the selected Docker Hub repository. The image tag is set automatically to {task type}-{project name}-{task name}-v{version}.',
    tooltip:
      'Targets are configured by your DevOps team in Account Settings and selected here by environment alias.',
    labelTooltip: 'Pre-configured Docker Hub destination, identified by its prod/dev alias.',
    placeholder: 'Select a Docker Hub target',
    emptyHint:
      'No Docker Hub targets configured. Ask a DevOps admin to add one under Account Settings → External Deploy - Targets (Docker Hub).',
    settingsKey: 'dockerhub_targets',
    requiredField: 'repository',
    sharedConfig: (target) => ({
      dockerhub_repository: (target.repository || '').trim(),
      dockerhub_alias: target.alias || null,
    }),
    summary: (target) =>
      `Target: ${target.alias || 'dev'} (${target.repository}). ${IMAGE_TAG_HINT}`,
  },
  acr: {
    title: 'Azure ACR target',
    description:
      'The inference image is built and pushed to the selected Azure Container Registry repository. The image tag is set automatically to {task type}-{project name}-{task name}-v{version}. Auth uses the Service Principal configured in Account Settings.',
    tooltip:
      'Targets are configured by your DevOps team in Account Settings (Azure - ACR) and selected here by environment alias.',
    labelTooltip: 'Pre-configured ACR destination, identified by its prod/dev alias.',
    placeholder: 'Select an ACR target',
    emptyHint:
      'No ACR targets configured. Ask a DevOps admin to add one under Account Settings → External Deploy - Targets (Azure - ACR).',
    settingsKey: 'azure_acr_targets',
    requiredField: 'uri',
    sharedConfig: (target) => ({
      acr_repository_uri: (target.uri || '').trim(),
      acr_auth_mode: target.auth_mode || 'same_subscription',
      acr_alias: target.alias || null,
    }),
    summary: (target) => `Target: ${target.alias || 'dev'} (service-principal). ${IMAGE_TAG_HINT}`,
  },
};

/** Configured targets for a push artifact type, from the raw org settings. */
export function registryTargetsFor(artifactType, settings) {
  const spec = REGISTRY_TARGETS[artifactType];
  if (!spec || !settings) return [];
  const targets = settings[spec.settingsKey];
  return Array.isArray(targets) ? targets.filter((t) => t && t[spec.requiredField]) : [];
}

/** Docker Hub push is AWS-only in this version. */
export function isDockerHubCloudBlocked(artifactType, cloudProvider) {
  return (
    artifactType === 'dockerhub' &&
    ['gcp', 'google', 'azure'].includes((cloudProvider || '').toLowerCase())
  );
}
